import { JobSecurityRight } from './job.security.module';
import { TaskSecurityRight } from './task.security.module';

export type JobSection = 'tasks' | 'items' | 'sors';
export type JobSectionVerb = 'view' | 'create' | 'edit' | 'delete';

export const JobSectionRightIds: Record<JobSection, Record<JobSectionVerb, JobSecurityRight>> = {
    tasks: { view: JobSecurityRight.TasksView, create: JobSecurityRight.TasksCreate, edit: JobSecurityRight.TasksEdit, delete: JobSecurityRight.TasksDelete },
    items: { view: JobSecurityRight.ItemsView, create: JobSecurityRight.ItemsCreate, edit: JobSecurityRight.ItemsEdit, delete: JobSecurityRight.ItemsDelete },
    sors: { view: JobSecurityRight.SorsView, create: JobSecurityRight.SorsCreate, edit: JobSecurityRight.SorsEdit, delete: JobSecurityRight.SorsDelete },
};

export const JOB_SECTION_RIGHTS: JobSecurityRight[] = Object.values(JobSectionRightIds).flatMap(section => Object.values(section));

/** Create, Edit and Delete always come with View: a section a user cannot open cannot be changed either. */
function sectionRights(ids: Record<JobSectionVerb, JobSecurityRight>, view: boolean, create: boolean, edit: boolean, remove: boolean): JobSecurityRight[] {

    const writes = [
        create ? ids.create : undefined,
        edit ? ids.edit : undefined,
        remove ? ids.delete : undefined,
    ].filter(right => right !== undefined);

    return view || writes.length ? [ids.view, ...writes] : [];
}

/**
 * The section rights a role held in effect before they existed, read off the job and task rights that gated
 * the same tabs and buttons. Used once per role: system roles in their class, custom roles by the tenant
 * upgrade. After that the sections are set on their own and never re-derived.
 */
export function deriveJobSectionRights(rights: number[]): JobSecurityRight[] {

    const has = (right: number) => rights.includes(right);
    const jobManage = has(JobSecurityRight.Manage);
    const manageTasks = has(TaskSecurityRight.ManageManualTasks);
    const deleteTasks = has(TaskSecurityRight.DeleteMyTasks) || has(TaskSecurityRight.DeleteAnyTasks);

    return [
        ...sectionRights(JobSectionRightIds.tasks, has(TaskSecurityRight.CanAccess), manageTasks, manageTasks, deleteTasks),
        ...sectionRights(JobSectionRightIds.items, has(JobSecurityRight.CanAccess), jobManage, jobManage, jobManage),
        ...sectionRights(JobSectionRightIds.sors, has(JobSecurityRight.CanAccess), jobManage, jobManage, jobManage),
    ];
}

/** A system role's rights with its section rights replaced by the derived ones, so a whole-module spread cannot grant more. */
export function withDerivedJobSectionRights(rights: number[]): number[] {

    return [
        ...rights.filter(right => !JOB_SECTION_RIGHTS.includes(right)),
        ...deriveJobSectionRights(rights),
    ];
}
